import { Flex, Group, rem, Skeleton, Stack, Text, Title } from "@mantine/core";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";

/**
 * Esqueleto de carregamento da aba WhatsApp (Configurações).
 *
 * Espelha a estrutura real dos itens carregados — o cartão de cabeçalho
 * (`HeaderCard`) com status/número e a lista de verificações de saúde
 * (`HealthChecksBlock`). Rótulos fixos dos cartões ("Conectado desde",
 * "Saúde da conta WhatsApp", etc.) são renderizados como texto real e os
 * skeletons cobrem apenas os valores dependentes do setor (status, badge,
 * nome/número, datas e linhas de verificação).
 *
 * Renderiza sem o shell da página (título + abas) porque é montado dentro do
 * `<Outlet>` de `SettingsPage`, tanto como fallback de dados quanto como
 * fallback de carregamento do chunk (Suspense) da rota.
 */
export function WhatsAppStatusPageSkeleton() {
  const { isMobileScreen } = useMobileScreen();

  return (
    <Stack>
      {/* HeaderCard — cartão de status da conexão. */}
      <Flex
        direction="column"
        gap={rem(16)}
        p={isMobileScreen ? rem(16) : rem(20)}
        style={{
          background: "var(--mantine-color-background_3-0)",
          border: "1px solid var(--mantine-color-default-border)",
          borderRadius: rem(12),
        }}>
        <Flex
          align={isMobileScreen ? "stretch" : "center"}
          direction={isMobileScreen ? "column" : "row"}
          justify="space-between"
          wrap="wrap"
          gap={rem(12)}>
          <Flex
            gap={rem(14)}
            align="flex-start"
            style={{ flex: 1, minWidth: 0 }}>
            {/* Ícone/estado da conexão (dinâmico). */}
            <Skeleton
              h={rem(44)}
              w={rem(44)}
              radius={rem(10)}
              style={{ flexShrink: 0 }}
            />
            <Stack
              gap={rem(6)}
              style={{ flex: 1, minWidth: 0 }}>
              <Group
                gap={rem(8)}
                align="center"
                wrap="wrap">
                {/* Status (Conectado/…) + badge do tipo de API. */}
                <Skeleton
                  h={rem(20)}
                  w={rem(110)}
                  radius="sm"
                />
                <Skeleton
                  h={rem(20)}
                  w={rem(84)}
                  radius="sm"
                />
              </Group>
              {/* Nome de exibição · número. */}
              <Skeleton
                h={rem(13)}
                w={rem(200)}
                radius="xl"
              />
            </Stack>
          </Flex>
          {/* Botão Desconectar (depende de permissão). */}
          <Skeleton
            h={rem(34)}
            w={isMobileScreen ? "100%" : rem(132)}
            radius="sm"
            style={{ flexShrink: 0 }}
          />
        </Flex>

        <Flex
          gap={rem(24)}
          wrap="wrap"
          style={{
            borderTop: "1px solid var(--mantine-color-default-border)",
            paddingTop: rem(14),
          }}>
          <Stack gap={rem(6)}>
            <Text
              fz={rem(11)}
              fw={700}
              c="dimmed"
              style={{ letterSpacing: "0.5px", textTransform: "uppercase" }}>
              Conectado desde
            </Text>
            <Skeleton
              h={rem(14)}
              w={rem(120)}
              radius="xl"
            />
          </Stack>
          <Stack gap={rem(6)}>
            <Text
              fz={rem(11)}
              fw={700}
              c="dimmed"
              style={{ letterSpacing: "0.5px", textTransform: "uppercase" }}>
              Qualidade do número
            </Text>
            <Skeleton
              h={rem(20)}
              w={rem(64)}
              radius="sm"
            />
          </Stack>
        </Flex>
      </Flex>

      {/* HealthChecksBlock — verificações de saúde da conta. */}
      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        gap={rem(8)}>
        <Title
          order={3}
          fz={rem(18)}
          fw={700}
          ff="heading"
          c="var(--mantine-color-plain_text-0)"
          style={{ flex: 1, minWidth: 0 }}>
          Saúde da conta WhatsApp
        </Title>
        <Skeleton
          h={rem(28)}
          w={rem(96)}
          radius="sm"
          style={{ flexShrink: 0 }}
        />
      </Flex>

      <Stack gap={rem(10)}>
        {Array.from({ length: 3 }).map((_, index) => (
          <HealthCheckRowSkeleton
            key={index}
            isMobileScreen={isMobileScreen}
          />
        ))}
      </Stack>

      <Group gap={rem(6)}>
        <Text
          fz={rem(12)}
          c="dimmed">
          Última verificação:
        </Text>
        <Skeleton
          h={rem(12)}
          w={rem(90)}
          radius="xl"
        />
      </Group>
    </Stack>
  );
}

/**
 * Linha de verificação de saúde (espelha `HealthCheckRow`): ícone de severidade,
 * título e descrição — todos dependentes do resultado da verificação.
 */
function HealthCheckRowSkeleton({
  isMobileScreen,
}: {
  isMobileScreen: boolean;
}) {
  return (
    <Flex
      direction={isMobileScreen ? "column" : "row"}
      gap={rem(14)}
      align={isMobileScreen ? "stretch" : "flex-start"}
      p={rem(16)}
      style={{
        background: "var(--mantine-color-background_3-0)",
        border: "1px solid var(--mantine-color-default-border)",
        borderRadius: rem(10),
      }}>
      <Flex
        gap={rem(14)}
        align="flex-start"
        style={{ flex: 1, minWidth: 0 }}>
        <Skeleton
          h={rem(32)}
          w={rem(32)}
          radius={rem(8)}
          style={{ flexShrink: 0 }}
        />
        <Stack
          gap={rem(6)}
          style={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            h={rem(14)}
            w="45%"
            radius="xl"
          />
          <Skeleton
            h={rem(11)}
            radius="xl"
          />
          <Skeleton
            h={rem(11)}
            w="80%"
            radius="xl"
          />
        </Stack>
      </Flex>
    </Flex>
  );
}

export default WhatsAppStatusPageSkeleton;
